// Client-side helper to track stats.
//
// Uses navigator.sendBeacon when available so events survive page unload
// (the common "copy command → switch to Minecraft" flow). Falls back to
// fetch with keepalive for browsers without sendBeacon.
type StatName =
  | 'totalVisits'
  | 'playerDatUploads'
  | 'playerDatDownloads'
  | 'levelDatUploads'
  | 'levelDatDownloads'
  | 'enchantmentBuilderUses'
  | 'uuidLookups'
  | 'commandsCopied'
  | 'skinsDownloaded'
  | 'signsCreated'
  | 'bannersCreated'
  | 'shieldsCreated'
  | 'tellrawsCreated'
  | 'lootTablesCreated'
  | 'potionsCreated'
  | 'booksCreated'
  | 'armorBuilderUses'
  | 'fireworksCreated'
  | 'armorStandUses'
  | 'mobBuilderUses'
  | 'recipesViewed'
  | 'idListCopies'
  | 'toolViewed'
  | 'designSaved'
  | 'designShared'
  | 'designExported'
  | 'designDownloaded';

const ENDPOINT = '/api/event';

function sendEvent(body: Record<string, unknown>): void {
  try {
    const payload = JSON.stringify(body);
    const beacon = typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function';
    if (beacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
    // Fallback: fetch with keepalive so the request survives tab close.
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {
      // Silent — tracking is fire-and-forget.
    });
  } catch {
    // Never let tracking failures break the UI.
  }
}

export async function trackStat(stat: StatName, details?: Record<string, unknown>): Promise<void> {
  const body: Record<string, unknown> = { stat };
  if (details) body.details = details;
  sendEvent(body);
}

// Records one anonymous site visit per page load. Deduped only with an in-memory
// flag, which covers SPA navigations within a load (AuthProvider stays mounted).
// Deliberately NO cookie and NO sessionStorage: nothing is stored on the
// visitor's device and no identifier is kept, so this falls outside PECR/GDPR
// consent (it's a purely server-side aggregate counter). The trade-off is that a
// full page reload counts as a new visit — an acceptable, consistent overcount.
let visitRecorded = false;
export function trackVisit(): void {
  if (visitRecorded) return;
  visitRecorded = true;
  sendEvent({ stat: 'totalVisits' });
}

// Track player.dat upload and record the UUID for caching
export async function trackPlayerDatUploadWithUUID(uuid: string): Promise<void> {
  sendEvent({ stat: 'playerDatUploads', uuid });
}

export const trackPlayerDatUpload = () => trackStat('playerDatUploads');
export const trackPlayerDatDownload = () => trackStat('playerDatDownloads');
export const trackLevelDatUpload = () => trackStat('levelDatUploads');
export const trackLevelDatDownload = () => trackStat('levelDatDownloads');
export const trackUUIDLookup = () => trackStat('uuidLookups');

export const trackCommandCopy = (details?: {
  command?: string;
  category?: string;
  item?: string;
  entity?: string;
  version?: string;
  source?: string;
  subcommands?: string[];
  chainLength?: number;
}) => trackStat('commandsCopied', details);

export const trackEnchantmentBuilderUse = (details?: {
  item?: string;
  enchantments?: string[];
  enchantmentCount?: number;
  type?: string;
  allowImpossible?: boolean;
  bypassLevelLimits?: boolean;
  version?: string;
}) => trackStat('enchantmentBuilderUses', details);

export const trackSkinDownload = (details?: {
  modelType?: string;
}) => trackStat('skinsDownloaded', details);

export const trackSignCreated = (details?: {
  commandType?: string;
}) => trackStat('signsCreated', details);

export const trackBannerCreated = (details?: {
  commandFormat?: string;
}) => trackStat('bannersCreated', details);

export const trackShieldCreated = (details?: {
  commandFormat?: string;
}) => trackStat('shieldsCreated', details);

export const trackTellrawCreated = (details?: {
  componentCount?: number;
  hasClickEvents?: boolean;
  hasHoverEvents?: boolean;
}) => trackStat('tellrawsCreated', details);

export const trackLootTableCreated = (details?: {
  tableType?: string;
  poolCount?: number;
  version?: string;
}) => trackStat('lootTablesCreated', details);

export const trackPotionCreated = (details?: {
  mode?: string;
  itemType?: string;
  effectCount?: number;
  version?: string;
}) => trackStat('potionsCreated', details);

export const trackBookCreated = (details?: {
  pageCount?: number;
  bookType?: string;
  hasFormatting?: boolean;
  version?: string;
}) => trackStat('booksCreated', details);

export const trackArmorBuilderUse = (details?: {
  mode?: string;
  material?: string;
  hasTrim?: boolean;
  enchantmentCount?: number;
  version?: string;
}) => trackStat('armorBuilderUses', details);

export const trackFireworkCreated = (details?: {
  explosionCount?: number;
  flightDuration?: number;
  version?: string;
}) => trackStat('fireworksCreated', details);

export const trackArmorStandUse = (details?: {
  hasEquipment?: boolean;
  presetUsed?: string;
  version?: string;
}) => trackStat('armorStandUses', details);

export const trackMobBuilderUse = (details?: {
  mobType?: string;
  equipmentCount?: number;
  hasCustomName?: boolean;
  mode?: string;
  version?: string;
}) => trackStat('mobBuilderUses', details);

export const trackRecipeViewed = (details?: {
  recipeId?: string;
  version?: string;
}) => trackStat('recipesViewed', details);

export const trackIdListCopy = (details?: {
  dataType?: string;
  copyType?: string;
}) => trackStat('idListCopies', details);

// ── Funnel tracking (view → save/share → copy) ──

export const trackToolView = (tool: string) => trackStat('toolViewed', { tool });
export const trackDesignSaved = (tool: string) => trackStat('designSaved', { tool });
export const trackDesignShared = (tool: string) => trackStat('designShared', { tool });
// Export a design as a JSON config file, or download an asset (image/PNG). Keyed by tool
// slug like the other funnel stats; the "user took the output away" terminal actions.
export const trackDesignExported = (tool: string) => trackStat('designExported', { tool });
export const trackDesignDownloaded = (tool: string) => trackStat('designDownloaded', { tool });
